// src/pages/NetworkSetup.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { deleteField, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuidv4 } from "uuid";
import PageHeader from "../components/PageHeader";
import { collectNetworkQuestionGroups, findNetworkQuestionGroup } from "../utils/networkQuestionGroups";

type SurveyElement = {
  type?: string;
  name?: string;
  title?: string | { default?: string };
  visibleIf?: string;
  firestoreListId?: string;
  dataListId?: string;
  elements?: SurveyElement[];
  templateElements?: SurveyElement[];
};

type SurveyJSON = {
  title?: string;
  pages?: { elements?: SurveyElement[] }[];
};

const PANEL_TYPES = new Set(["panel", "paneldynamic"]);
const QUESTION_TYPES = new Set(["text", "dropdown", "radiogroup", "tagbox", "checkbox"]);

interface Option {
  value: string;
  label: string;
  indent?: boolean;
  isPanel?: boolean;
  questionTitle?: string;
  questionName?: string;
  helpers?: string[];
}

type NetworkConfig = {
  id: string;
  source: string;
  target: string;
  updatedAt?: string;
};

const getConfigSortScore = (cfg: NetworkConfig): number => {
  if (!cfg) return 0;
  const parsed = cfg.updatedAt ? Date.parse(cfg.updatedAt) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const dedupeConfigs = (list: NetworkConfig[]): NetworkConfig[] => {
  const seen = new Set<string>();
  const result: NetworkConfig[] = [];
  for (const cfg of list) {
    const key = `${cfg.source}__${cfg.target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(cfg);
  }
  return result;
};

const sortConfigs = (list: NetworkConfig[]): NetworkConfig[] =>
  [...list].sort((a, b) => getConfigSortScore(b) - getConfigSortScore(a));

const normalizeConfig = (
  docId: string,
  cfg: any,
  idx: number
): NetworkConfig | null => {
  if (!cfg?.source || !cfg?.target) return null;
  return {
    id: typeof cfg.id === "string" && cfg.id.trim() ? cfg.id : `${docId}-${idx}`,
    source: cfg.source,
    target: cfg.target,
    updatedAt: typeof cfg.updatedAt === "string" ? cfg.updatedAt : undefined,
  };
};

const extractConfigsFromData = (docId: string, data?: any): NetworkConfig[] => {
  if (!data) return [];
  const raw = Array.isArray(data.configs) ? data.configs : [];
  const parsed = raw
    .map((cfg: any, idx: number) => normalizeConfig(docId, cfg, idx))
    .filter(Boolean) as NetworkConfig[];

  if (data.source && data.target) {
    parsed.push({
      id: `${docId}-legacy`,
      source: data.source,
      target: data.target,
      updatedAt: data.updatedAt,
    });
  }

  return sortConfigs(dedupeConfigs(parsed));
};

const serializeConfigs = (list: NetworkConfig[]) =>
  list.map((cfg) => {
    const payload: { id: string; source: string; target: string; updatedAt?: string } = {
      id: cfg.id,
      source: cfg.source,
      target: cfg.target,
    };
    if (cfg.updatedAt) payload.updatedAt = cfg.updatedAt;
    return payload;
  });

const writeConfigs = async (surveyId: string, list: NetworkConfig[]) => {
  const cfgRef = doc(db, "networkConfigs", surveyId);
  await setDoc(
    cfgRef,
    {
      configs: serializeConfigs(list),
      source: deleteField(),
      target: deleteField(),
      updatedAt: deleteField(),
    },
    { merge: true }
  );
};

export default function NetworkSetup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<SurveyJSON | null>(null);
  const [surveyName, setSurveyName] = useState<string>("");
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [configs, setConfigs] = useState<NetworkConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshConfigs = useCallback(async () => {
    if (!id) {
      setConfigs([]);
      return;
    }

    setLoadingConfigs(true);
    try {
      const cfgRef = doc(db, "networkConfigs", id);
      const snap = await getDoc(cfgRef);
      if (!snap.exists()) {
        setConfigs([]);
      } else {
        setConfigs(extractConfigsFromData(snap.id, snap.data()));
      }
    } catch (err) {
      console.error("[NetworkSetup] ❌ Failed to load network configs", err);
      setConfigs([]);
    } finally {
      setLoadingConfigs(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (!id) return;
      console.log("[NetworkSetup] 🔍 Loading survey for:", id);

      const sRef = doc(db, "surveys", id);
      const snap = await getDoc(sRef);
      if (!snap.exists()) {
        console.warn("[NetworkSetup] ⚠️ Survey not found:", id);
        return;
      }

      const data = snap.data() as any;
      const json: SurveyJSON =
        data?.json || data?.creatorJson || data?.definition || data?.schema;

      console.log("[NetworkSetup] ✅ Survey doc found:", {
        title: data?.title,
        hasJson: !!json,
        jsonKeys: json ? Object.keys(json) : [],
      });

      setSurvey(json ?? null);
      setSurveyName(json?.title || data?.title || "(Untitled Survey)");
    })();
  }, [id]);

  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  const getTitle = (el?: SurveyElement): string => {
    if (!el) return "";
    if (typeof el.title === "string" && el.title.trim()) return el.title.trim();
    if (el.title && typeof el.title === "object") {
      return el.title.default?.trim?.() || el.name || "";
    }
    return el.name || "";
  };

  const { sourceOptions, targetOptions } = useMemo(() => {
    const src: Option[] = [];
    const tgt: Option[] = [];
    if (!survey?.pages) return { sourceOptions: src, targetOptions: tgt };

    const groupOptionByFirstMember = new Map<string, Option>();
    collectNetworkQuestionGroups(survey)
      .filter((group) => !group.parentName)
      .forEach((group) => {
        const firstMember = group.members[0]?.name;
        if (!firstMember) return;
        groupOptionByFirstMember.set(firstMember, {
          value: group.id,
          label: `${group.title} — all countries`,
          questionTitle: `${group.title} — all countries`,
          questionName: group.baseName,
          helpers: ["grouped dropdown"],
        });
      });

    const walk = (el: SurveyElement, indent = false) => {
      const type = el.type?.toLowerCase() || "";
      if (!el.name) return;

      const title = getTitle(el);
      const isPanel = PANEL_TYPES.has(type);
      let helperLabel = "";
      const helpers: string[] = [];

      if (isPanel) {
        helperLabel = type === "paneldynamic" ? "dynamic panel" : "panel";
        helpers.push(helperLabel);
      }

      let label = title || el.name;
      if (helperLabel) label += ` — ${helperLabel}`;
      label += ` (${el.name})`;

      const opt: Option = {
        value: el.name,
        label,
        indent,
        isPanel,
        questionTitle: title || el.name,
        questionName: el.name,
        helpers,
      };

      const groupOpt = groupOptionByFirstMember.get(el.name);
      if (groupOpt) {
        src.push(groupOpt);
        tgt.push(groupOpt);
      }

      if (isPanel) {
        src.push(opt);
        tgt.push(opt);
      }

      if (QUESTION_TYPES.has(type)) {
        src.push(opt);
        tgt.push(opt);
      }

      const children = el.templateElements || el.elements || [];
      for (const child of children) walk(child, true);
    };

    for (const page of survey.pages) {
      for (const el of page.elements || []) walk(el, false);
    }

    console.log("[NetworkSetup] 🧩 Generated question options:", {
      sourceCount: src.length,
      targetCount: tgt.length,
      firstSource: src[0]?.value,
      firstTarget: tgt[0]?.value,
    });

    return { sourceOptions: src, targetOptions: tgt };
  }, [survey]);

  const filteredTargetOptions = targetOptions.filter((opt) => opt.value !== source);
  const optionLookup = useMemo(() => {
    const map = new Map<string, Option>();
    [...sourceOptions, ...targetOptions].forEach((opt) => {
      if (!map.has(opt.value)) {
        map.set(opt.value, opt);
      }
    });
    return map;
  }, [sourceOptions, targetOptions]);

  const formatQuestionDetails = useCallback(
    (value: string) => {
      const group = findNetworkQuestionGroup(survey || undefined, value);
      if (group) {
        return {
          title: `${group.title} — all countries`,
          meta: `grouped dropdown • ${group.baseName}`,
        };
      }

      const opt = optionLookup.get(value);
      if (!opt) return { title: value, meta: "" };

      const metaParts: string[] = [];
      if (opt.helpers?.length) metaParts.push(opt.helpers.join(", "));
      if (opt.questionName) metaParts.push(opt.questionName);

      return {
        title: opt.questionTitle || opt.label,
        meta: metaParts.join(" • "),
      };
    },
    [optionLookup, survey]
  );

  useEffect(() => {
    if (!source && !target && configs.length > 0) {
      setSource(configs[0].source);
      setTarget(configs[0].target);
    }
  }, [configs, source, target]);

  const goToNetwork = useCallback(
    (src: string, tgt: string) => {
      if (!id) return;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      navigate(`/network/${id}/${src}/${tgt}`);
    },
    [id, navigate]
  );
  const goToNetworkDetails = useCallback(
    (src: string, tgt: string) => {
      if (!id) return;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      navigate(`/network/${id}/${src}/${tgt}/details`);
    },
    [id, navigate]
  );

  const saveAndGo = async () => {
    if (!id || !source || !target) return;
    console.log("[NetworkSetup] 💾 Saving network config:", { id, source, target });

    try {
      setSavingConfig(true);
      const exists = configs.some((cfg) => cfg.source === source && cfg.target === target);

      if (!exists) {
        const newConfig: NetworkConfig = {
          id: uuidv4(),
          source,
          target,
          updatedAt: new Date().toISOString(),
        };
        const nextConfigs = sortConfigs(dedupeConfigs([newConfig, ...configs]));
        await writeConfigs(id, nextConfigs);
        setConfigs(nextConfigs);
      }

      console.log("[NetworkSetup] ✅ Config saved/navigate to graph view");
      goToNetwork(source, target);
    } catch (err) {
      console.error("[NetworkSetup] ❌ Failed to save config", err);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSelectConfig = (cfg: NetworkConfig) => {
    if (!id) return;
    setSource(cfg.source);
    setTarget(cfg.target);
    goToNetwork(cfg.source, cfg.target);
  };

  const handleDeleteConfig = async (cfg: NetworkConfig) => {
    if (!cfg?.id || !id) return;

    try {
      setDeletingId(cfg.id);
      const nextConfigs = configs.filter((item) => item.id !== cfg.id);
      await writeConfigs(id, nextConfigs);
      setConfigs(nextConfigs);
    } catch (err) {
      console.error("[NetworkSetup] ❌ Failed to delete config", err);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    console.log("[NetworkSetup] ⚙️ Current state changed:", { source, target });
  }, [source, target]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={4} sx={{ maxWidth: 960, mx: "auto" }}>
        <PageHeader
          title="Network Graph Visualiser Setup"
          subtitle={surveyName ? `Survey: ${surveyName}` : undefined}
        >
          <Typography color="text.secondary">
            Choose which questions represent the <b>source</b> (who is nominating) and{" "}
            <b>target</b> (who they nominated). Use the quick links below to revisit past combinations,
            or define a new pairing and click <b>“Create &amp; View Network”</b>.
          </Typography>
        </PageHeader>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Available network graphs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These quick links open saved question pairings. Remove any that you no longer
            need.
          </Typography>

          {loadingConfigs ? (
            <Typography color="text.secondary">Loading saved configurations…</Typography>
          ) : configs.length === 0 ? (
            <Typography color="text.secondary">
              No saved configurations yet. Once you create a new network below we’ll save it
              automatically for quick access.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {configs.map((cfg) => {
                const sourceInfo = formatQuestionDetails(cfg.source);
                const targetInfo = formatQuestionDetails(cfg.target);
                return (
                  <Box
                    key={`${cfg.id}-${cfg.source}-${cfg.target}`}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="overline" color="text.secondary">
                            Source
                          </Typography>
                          <Typography variant="subtitle2">{sourceInfo.title}</Typography>
                          {sourceInfo.meta && (
                            <Typography variant="body2" color="text.secondary">
                              {sourceInfo.meta}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="overline" color="text.secondary">
                            Target
                          </Typography>
                          <Typography variant="subtitle2">{targetInfo.title}</Typography>
                          {targetInfo.meta && (
                            <Typography variant="body2" color="text.secondary">
                              {targetInfo.meta}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSelectConfig(cfg)}
                        >
                          View this network
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => goToNetworkDetails(cfg.source, cfg.target)}
                        >
                          View details
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          startIcon={<CloseIcon fontSize="small" />}
                          onClick={() => handleDeleteConfig(cfg)}
                          disabled={deletingId === cfg.id}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Box sx={{ maxWidth: 800 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Create a new network graph
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the two questions you would like to visualise together. When you create
            the network we’ll save the combination for future quick access.
          </Typography>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel id="src-label">Source Question</InputLabel>
              <Select
                labelId="src-label"
                label="Source Question"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {sourceOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={opt.indent ? { pl: 4 } : {}}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="tgt-label">Target Panel / Question</InputLabel>
            <Select
              labelId="tgt-label"
              label="Target Panel / Question"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              {filteredTargetOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    pl: opt.indent ? 4 : 2,
                    fontStyle: opt.isPanel ? "italic" : "normal",
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              onClick={saveAndGo}
              disabled={!id || !source || !target || savingConfig}
              sx={{ px: 3 }}
            >
              {savingConfig ? "Saving…" : "Create & View Network"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  </Box>
  );
}
